import { useState, useEffect, FC, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from 'axios';
import css from './index.module.css';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import Popup from "@components/popup";
import * as Types from '../../types';
import InputMask from 'react-input-mask';
import useUserData from "@hooks/useUserData";

interface Modal {
    showModal: boolean; 
    setShowModal: any;
    info?: Types.Container;
    setStep?: any;
}

export const UpdateEmail:FC<Modal> = ({ showModal, setShowModal }) => {
    const { userData } = useUserData();
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    const handleSubmit = async (event: any) => {
        event.preventDefault();
        const formData = new FormData(event.target);

        axios.post(`${import.meta.env.VITE_API_URL}/user/email`, formData, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('authToken')
            }
        }).then(_ => {       
            toast.success("Информация успешно обновлена");
            setShowModal(false);
        }).catch(_ => {
            toast.error(t('gtm.err'));
        });
    };
  
    return (
        <Popup showPopup={showModal} setShowPopup={setShowModal}>
            <h5 style={{ fontSize: '20px', marginBottom: 10, marginTop: 0 }}>Обновить электронную почту</h5>
            <p style={{ color: "var(--text-color)", marginBottom: "15px", lineHeight: "1.5" }}>
                Вы можете обновить адрес электронной почты. Пожалуйста, обратите внимание, что это действие необратимо.
            </p>
            
            <form onSubmit={handleSubmit}>
                <div className={css.inputBlock} style={{ marginBottom: 10 }}>
                    <label>Текущая электронная почта</label>
                    <input type="email" placeholder="Электронная почта" value={userData?.email} disabled/>
                </div>

                <div className={css.inputBlock} style={{ marginBottom: 10 }}>
                    <label>Новая электронная почта</label>
                    <input type="email" placeholder="Новая электронная почта" name="email" required/>
                </div>

                <button style={{ background: "#6258ee", width: '100%', marginTop: 20 }} className="btn" type="submit">{t('gtm.update')}</button>
            </form>
        </Popup>
    );
}

export const UpdatePhone:FC<Modal> = ({ showModal, setShowModal }) => {
    const { userData } = useUserData();
    const [step, setStep] = useState<number>(0);
    const [phone, setPhone] = useState('');
    const { t } = useTranslation();
    
    useEffect(() => {
        if (step === 2) setShowModal(false);
    }, [step]);

    const handlePhone = (event: any) => {
        setPhone(event.target.value);
    };

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        formData.append('phone', phone);

        axios.post(`${import.meta.env.VITE_API_URL}/user/phone`, formData, {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('authToken')
            }
        }).then(_ => {       
            toast.success("Введите код из звонка");
            setStep(1);
        }).catch(_ => {
            toast.error("Возникла ошибка");
        });
    };
  
    return (
        <Popup showPopup={showModal} setShowPopup={setShowModal}>
            {step === 0 ?
                <>
                    <h5 style={{ fontSize: '20px', marginBottom: 10, marginTop: 0 }}>Обновить телефон</h5>
                    <p style={{color: "var(--text-color)"}}>Вы можете обновить привязанный телефон. Пожалуйста, обратите внимание, что это действие необратимо.</p>

                    <form onSubmit={handleSubmit}>
                        <div className={css.inputBlock} style={{ marginBottom: 10 }}>
                            <label>Текущий номер телефона</label>
                            <InputMask 
                                mask="+7 (999) 999-99-99" 
                                value={userData?.phone || ''} 
                                disabled 
                                placeholder="Текущий номер телефона" 
                            />
                        </div>
                        <div className={css.inputBlock} style={{ marginBottom: 10 }}>
                            <label>Новый номер телефона</label>
                            <InputMask placeholder="Введите номер телефона" mask="+7 (999) 999-99-99" value={phone} onChange={handlePhone} required />
                        </div>

                        <button style={{ background: "#6258ee", width: '100%', marginTop: 10 }} className="btn" type="submit">{t('gtm.update')}</button>
                    </form>
                </>
            :
                <>
                    <h5 style={{ fontSize: '20px', marginBottom: 10, marginTop: 0 }}>Введите код</h5>
                    <p style={{color: "var(--text-color)"}}>Введите ниже код, который продиктует робот в звонке.</p>

                    <form className={css.form}>
                        <SmsCode phone={phone} setStep={setStep} />
                        <button type="submit" style={{ width: '100%', marginTop: 20 }}>Повторить отправку</button>
                    </form>
                </>
            }
        </Popup>
    );
}

function SmsCode({ phone, setStep }: { phone: string, setStep: any }) {
    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [codeEntered, setCodeEntered] = useState<boolean>(false);
    const inputRefs = useRef<any>([]);

    const handleChange = (e: any, index: number) => {
        const { value } = e.target;
        if (/^\d*$/.test(value)) {
            const newCode = [...code];
            newCode[index] = value.slice(0, 1);
            setCode(newCode);

            if (index < 5 && value !== '') {
                inputRefs.current[index + 1].focus();
            }

            if (newCode.every((digit) => digit !== '')) {
                setCodeEntered(true);
            } else {
                setCodeEntered(false);
            }
        }
    };

    const handleBackspace = (e: any, index: number) => {
        if (e.keyCode === 8 && index > 0 && code[index] === '') {
            const newCode = [...code];
            newCode[index - 1] = '';
            setCode(newCode);
            inputRefs.current[index - 1].focus();
            setCodeEntered(false);
        }
    };

    const renderInputs = () => {
        const inputs = [];
        for (let i = 0; i < 6; i++) {
            inputs.push(
                <input
                    key={i}
                    type="text"
                    maxLength={1}
                    value={code[i]}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleBackspace(e, i)}
                    ref={(el) => (inputRefs.current[i] = el)}
                />
            );
        }
        return inputs;
    };

    const sendCode = () => {
        const enteredCode = code.join('');
        axios.post(
            `${import.meta.env.VITE_API_URL}/user/phone/code`,
            { phone: phone, code: enteredCode },
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + localStorage.getItem('authToken')
                }
            }
        )
        .then(response => {
            toast.success('Номер телефона успешно обновлён');
            setStep(2);
        })
        .catch(() => {
            toast.error('Неверно введен код авторизации');
        });
    };

    useEffect(() => {
        if (codeEntered) {
            sendCode();
            setCodeEntered(false);
        }
    }, [codeEntered]);

    return (
        <div className={css.enterCode}>
            {renderInputs()}
        </div>
    );
}